import { useState, useEffect } from "react";
import { X, Cookie, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { ANALYTICS_CONSENT_EVENT } from "@/otel/loader";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("aroan-cookie-consent");
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem("aroan-cookie-consent", JSON.stringify(prefs));
    localStorage.setItem("aroan-cookie-consent-date", new Date().toISOString());
    
    // Here you would typically initialize analytics/marketing scripts based on preferences
    if (prefs.analytics) {
      window.dispatchEvent(new Event(ANALYTICS_CONSENT_EVENT));
    }
    if (prefs.marketing) {
      // Initialize marketing cookies
      console.log("Marketing cookies enabled");
    }
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    savePreferences(allAccepted);
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    savePreferences(necessaryOnly);
    setShowBanner(false);
  };

  const handleSaveSettings = () => {
    savePreferences(preferences);
    setShowSettings(false);
    setShowBanner(false);
  };

  const handleOpenSettings = () => {
    setShowSettings(true);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 bg-background/95 backdrop-blur-lg border-t border-border shadow-2xl animate-in slide-in-from-bottom duration-500">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
            {/* Icon */}
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Cookie className="w-6 h-6 text-primary" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">
                Nós usamos cookies 🍪
              </h3>
              <p className="text-sm text-foreground/80 leading-relaxed">
                Utilizamos cookies e tecnologias similares para melhorar sua experiência, 
                personalizar conteúdo, analisar tráfego e oferecer funcionalidades de redes sociais. 
                Ao clicar em "Aceitar Todos", você concorda com o uso de cookies conforme descrito em nossa{" "}
                <Link to="/privacidade" className="text-primary hover:underline font-medium">
                  Política de Privacidade
                </Link>.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <Button
                variant="outline"
                onClick={handleOpenSettings}
                className="gap-2"
              >
                <Settings className="w-4 h-4" />
                Configurar
              </Button>
              <Button
                variant="outline"
                onClick={handleAcceptNecessary}
              >
                Apenas Necessários
              </Button>
              <Button
                onClick={handleAcceptAll}
                className="bg-primary hover:bg-primary/90 text-background"
              >
                Aceitar Todos
              </Button>
            </div>

            {/* Close button */}
            <button
              onClick={handleAcceptNecessary}
              className="absolute top-4 right-4 md:relative md:top-0 md:right-0 text-foreground/40 hover:text-foreground transition-colors"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Cookie className="w-6 h-6 text-primary" />
              Configurações de Cookies
            </DialogTitle>
            <DialogDescription>
              Gerencie suas preferências de cookies. Você pode escolher quais categorias de cookies deseja aceitar.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-6">
            {/* Necessary Cookies */}
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Label className="text-base font-semibold">
                      Cookies Necessários
                    </Label>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                      Sempre Ativo
                    </span>
                  </div>
                  <p className="text-sm text-foreground/70 leading-relaxed">
                    Estes cookies são essenciais para o funcionamento do site e não podem ser desativados. 
                    Eles são geralmente definidos em resposta a ações suas, como configurar suas preferências 
                    de privacidade, fazer login ou preencher formulários.
                  </p>
                  <div className="mt-2 text-xs text-foreground/50">
                    Exemplos: autenticação, segurança, preferências de idioma
                  </div>
                </div>
                <Switch
                  checked={preferences.necessary}
                  disabled
                  className="mt-1"
                />
              </div>
            </div>

            <div className="border-t border-border" />

            {/* Analytics Cookies */}
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <Label htmlFor="analytics" className="text-base font-semibold">
                    Cookies de Análise
                  </Label>
                  <p className="text-sm text-foreground/70 leading-relaxed mt-2">
                    Estes cookies nos permitem contar visitas e fontes de tráfego para que possamos 
                    medir e melhorar o desempenho do nosso site. Eles nos ajudam a saber quais páginas 
                    são mais e menos populares e ver como os visitantes se movem pelo site.
                  </p>
                  <div className="mt-2 text-xs text-foreground/50">
                    Exemplos: Google Analytics, análise de comportamento do usuário
                  </div>
                </div>
                <Switch
                  id="analytics"
                  checked={preferences.analytics}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, analytics: checked })
                  }
                  className="mt-1"
                />
              </div>
            </div>

            <div className="border-t border-border" />

            {/* Marketing Cookies */}
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <Label htmlFor="marketing" className="text-base font-semibold">
                    Cookies de Marketing
                  </Label>
                  <p className="text-sm text-foreground/70 leading-relaxed mt-2">
                    Estes cookies podem ser definidos através do nosso site por nossos parceiros de 
                    publicidade. Eles podem ser usados por essas empresas para criar um perfil de seus 
                    interesses e mostrar anúncios relevantes em outros sites.
                  </p>
                  <div className="mt-2 text-xs text-foreground/50">
                    Exemplos: remarketing, anúncios personalizados, redes sociais
                  </div>
                </div>
                <Switch
                  id="marketing"
                  checked={preferences.marketing}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, marketing: checked })
                  }
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-border">
            <Button
              variant="outline"
              onClick={() => setShowSettings(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveSettings}
              className="flex-1 bg-primary hover:bg-primary/90 text-background"
            >
              Salvar Preferências
            </Button>
          </div>

          <div className="mt-4 text-center">
            <Link
              to="/privacidade"
              className="text-sm text-primary hover:underline"
            >
              Saiba mais em nossa Política de Privacidade
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
